import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import {
    getUser,
    enableUser,
    disableUser,
    deleteUser,
} from "@/services/userService";
import { type User } from "../../amplify/api/types";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserProfileForm } from "@/components/UserProfileForm";
import { UserGroupsForm } from "@/components/UserGroupsForm";
import { ArrowLeft, Power, PowerOff, Trash2 } from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
    CardDescription,
    //CardFooter,
} from "@/components/ui/card";

export function UserDetailPage() {
    const { username } = useParams<{ username: string }>();
    const navigate = useNavigate();
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    useEffect(() => {
        const loadUser = async () => {
            if (!username) return;

            try {
                setIsLoading(true);
                const userData = await getUser(username);
                setUser(userData);
            } catch (error) {
                toast.error(`Failed to load user: ${(error as Error).message}`);
            } finally {
                setIsLoading(false);
            }
        };

        loadUser();
    }, [username]);

    const handleUserUpdated = async () => {
        if (!username) return;

        try {
            const updatedUser = await getUser(username);
            setUser(updatedUser);
        } catch (error) {
            console.error("Error refreshing user data:", error);
        }
    };

    const handleEnableUser = async () => {
        if (!username || !user) return;

        try {
            setIsProcessing(true);
            const updatedUser = await enableUser(username);
            setUser(updatedUser);
            toast.success(`User ${username} has been enabled`);
        } catch (error) {
            toast.error(`Failed to enable user: ${(error as Error).message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDisableUser = async () => {
        if (!username || !user) return;

        try {
            setIsProcessing(true);
            const updatedUser = await disableUser(username);
            setUser(updatedUser);
            toast.success(`User ${username} has been disabled`);
        } catch (error) {
            toast.error(`Failed to disable user: ${(error as Error).message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!username) return;

        try {
            setIsProcessing(true);
            await deleteUser(username);
            toast.success(`User ${username} has been deleted`);
            navigate("/users");
        } catch (error) {
            toast.error(`Failed to delete user: ${(error as Error).message}`);
            setIsProcessing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="max-w-3xl mx-auto">
                <Card>
                    <CardContent className="flex justify-center py-8">
                        <div className="animate-pulse">
                            Loading user details...
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="max-w-3xl mx-auto">
                <Card>
                    <CardContent className="flex flex-col items-center py-8">
                        <div className="text-red-500 mb-4">User not found</div>
                        <Button onClick={() => navigate("/users")}>
                            Back to Users
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto">
            <div className="mb-6">
                <Button
                    variant="outline"
                    onClick={() => navigate("/users")}
                    className="flex items-center gap-2"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Users
                </Button>
            </div>

            <Card className="mb-6">
                <CardHeader className="flex flex-row items-start justify-between">
                    <div>
                        <CardTitle>User: {user.username}</CardTitle>
                        <CardDescription>
                            Status: {user.enabled ? "Enabled" : "Disabled"} •{" "}
                            {user.userStatus}
                        </CardDescription>
                    </div>
                    <div className="flex gap-2">
                        {user.enabled ? (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDisableUser}
                                disabled={isProcessing}
                                className="flex items-center gap-1"
                            >
                                <PowerOff className="h-4 w-4" />
                                Disable
                            </Button>
                        ) : (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleEnableUser}
                                disabled={isProcessing}
                                className="flex items-center gap-1"
                            >
                                <Power className="h-4 w-4" />
                                Enable
                            </Button>
                        )}
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setShowDeleteDialog(true)}
                            disabled={isProcessing}
                            className="flex items-center gap-1"
                        >
                            <Trash2 className="h-4 w-4" />
                            Delete
                        </Button>
                    </div>
                </CardHeader>
            </Card>

            <Card>
                <CardContent className="pt-6">
                    <Tabs defaultValue="profile" className="w-full">
                        <TabsList>
                            <TabsTrigger value="profile">Profile</TabsTrigger>
                            <TabsTrigger value="groups">Groups</TabsTrigger>
                        </TabsList>

                        <TabsContent value="profile" className="mt-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Edit Profile</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <UserProfileForm
                                        username={user.username}
                                        onSuccess={handleUserUpdated}
                                    />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="groups" className="mt-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Manage Groups</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <UserGroupsForm
                                        username={user.username}
                                        onSuccess={handleUserUpdated}
                                    />
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>

            <AlertDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Are you absolutely sure?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This action will permanently delete the user "
                            {user.username}". This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isProcessing}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteUser}
                            disabled={isProcessing}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isProcessing ? "Deleting..." : "Delete User"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
